import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Button,
  TextField,
  MenuItem,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Close, CurrencyExchangeOutlined, ArrowForwardRounded } from "@mui/icons-material";
import { adminExchangeCurrency } from "api/wallet";
import { GetLatestRate } from "api/currencyExchangeRate";

const CURRENCIES = ["THB", "LAK", "USD"];
const SYM = { THB: "฿", LAK: "₭", USD: "$" };
const MONO = "'Space Grotesk', ui-monospace, monospace";
const money = (n, c) => `${SYM[c] || ""}${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const initials = (s) => (s || "?").slice(0, 2).toUpperCase();

// Same display rule as the backend's formatExchangeRate: always "1 <stronger> = N
// <weaker>" (USD > THB > LAK), so the rate is flipped when converting from the weaker one.
const STRENGTH = { USD: 0, THB: 1, LAK: 2 };
const formatRate = (from, to, unitRate) => {
  const flip = STRENGTH[from] > STRENGTH[to];
  const value = flip ? 1 / unitRate : unitRate;
  return `1 ${flip ? to : from} = ${Number(value.toFixed(flip ? 2 : 4)).toLocaleString()} ${flip ? from : to}`;
};

// Admin converts one currency into another inside a customer's wallet. Uses the
// latest system exchange rate (same conversion + floor-to-cents as the backend's
// convertWalletCurrency, so the preview matches what is actually credited) and the
// customer is notified with the full breakdown once it is done.
export default function ExchangeCurrencyDialog({ open, onClose, onSuccess, balances = {}, blocked = {}, target = {} }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const accent = theme.palette.secondary.main;

  const [rate, setRate] = useState(null);
  const [fromCurrency, setFromCurrency] = useState("THB");
  const [toCurrency, setToCurrency] = useState("LAK");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setFromCurrency("THB");
    setToCurrency("LAK");
    setAmount("");
    setNote("");
    setError("");
    setSubmitting(false);
    GetLatestRate().then((res) => setRate(res?.data || null));
  }, [open]);

  // Keep the two currencies different: picking one that the other side already
  // holds swaps them instead of leaving an invalid same-currency pair.
  const pickFrom = (c) => {
    if (c === toCurrency) setToCurrency(fromCurrency);
    setFromCurrency(c);
  };
  const pickTo = (c) => {
    if (c === fromCurrency) setFromCurrency(toCurrency);
    setToCurrency(c);
  };

  const available = round2((balances[fromCurrency] || 0) - (blocked[fromCurrency] || 0));

  // Convert via THB as base — rate.LAK is LAK per 1 THB, rate.USD is THB per 1 USD.
  const calc = useMemo(() => {
    if (!rate) return null;
    const toTHB = (v, c) => (c === "THB" ? v : c === "USD" ? v * rate.USD : v / rate.LAK);
    const fromTHB = (v, c) => (c === "THB" ? v : c === "USD" ? v / rate.USD : v * rate.LAK);
    const nominal = fromTHB(toTHB(1, fromCurrency), toCurrency);
    const given = Number(amount) || 0;
    return { nominal, received: Math.floor(fromTHB(toTHB(given, fromCurrency), toCurrency) * 100) / 100 };
  }, [rate, fromCurrency, toCurrency, amount]);

  const given = Number(amount) || 0;
  const overBalance = given > available;
  const tooSmall = given > 0 && calc && calc.received <= 0;
  const canSubmit = !!calc && given > 0 && !overBalance && !tooSmall && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const res = await adminExchangeCurrency(target.id, fromCurrency, toCurrency, given, note.trim());
      setSubmitting(false);
      if (res && res.success) onSuccess && onSuccess(res);
      else setError((res && res.message) || t("common.error"));
    } catch (e) {
      setSubmitting(false);
      setError(e?.message || t("common.error"));
    }
  };

  const currencyField = (label, value, onChange) => (
    <TextField select fullWidth size="small" color="secondary" label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      {CURRENCIES.map((c) => (
        <MenuItem key={c} value={c}>
          {SYM[c]} {c} — {money(balances[c], c)}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
      <DialogContent sx={{ p: { xs: 2.25, sm: 3 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 40, height: 40, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", background: alpha(accent, 0.1), color: accent, flexShrink: 0 }}>
              <CurrencyExchangeOutlined />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.15 }}>{t("wallet.admin.exchangeCurrency")}</Typography>
          </Stack>
          <IconButton size="small" onClick={onClose} disabled={submitting}><Close fontSize="small" /></IconButton>
        </Box>

        {/* which customer this applies to */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mt: 1.75, p: "10px 14px", borderRadius: "14px", background: "#faf9fc", border: "1px solid #f0eef5" }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: "linear-gradient(135deg,#7c3aed,#4c1d95)", color: "#fff", fontFamily: MONO, fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {initials(target.name || target.id)}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontWeight: 700, fontSize: 14.5, lineHeight: 1.3 }}>{target.name || target.id}</Typography>
            <Typography noWrap sx={{ fontFamily: MONO, fontSize: 11.5 }} color="text.secondary">
              {target.id}{target.phone ? ` · ${target.phone}` : ""}
            </Typography>
          </Box>
        </Box>

        <Stack spacing={1.75} sx={{ mt: 1.75 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            {currencyField(t("wallet.admin.exchangeFrom"), fromCurrency, pickFrom)}
            <ArrowForwardRounded sx={{ color: "#b3aebd", flexShrink: 0 }} />
            {currencyField(t("wallet.admin.exchangeTo"), toCurrency, pickTo)}
          </Stack>

          <TextField
            fullWidth
            size="small"
            type="number"
            color="secondary"
            label={t("wallet.admin.exchangeAmount")}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={overBalance}
            helperText={`${t("wallet.admin.exchangeAvailable")}: ${money(available, fromCurrency)} ${fromCurrency}`}
          />

          {/* live preview: what the customer will receive, and at which rate */}
          <Box sx={{ px: 1.75, py: 1.5, borderRadius: "14px", background: "#faf9fc", border: "1px solid #f0eef5" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 11 }} color="text.secondary">{t("wallet.admin.exchangeRate")}</Typography>
                <Typography noWrap sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 13.5, lineHeight: "20px" }}>
                  {calc ? formatRate(fromCurrency, toCurrency, calc.nominal) : "…"}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right", minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 11 }} color="text.secondary">{t("wallet.admin.exchangeReceives")}</Typography>
                <Typography noWrap sx={{ fontFamily: MONO, fontWeight: 800, fontSize: 17, lineHeight: "22px", color: accent }}>
                  {money(calc && given > 0 ? calc.received : 0, toCurrency)} {toCurrency}
                </Typography>
              </Box>
            </Box>
          </Box>

          <TextField fullWidth multiline rows={2} size="small" color="secondary" label={t("wallet.admin.exchangeNote")} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("wallet.admin.exchangeNotePlaceholder")} />

          <Alert severity="info" sx={{ borderRadius: "12px" }}>{t("wallet.admin.exchangeNotifyInfo")}</Alert>
          {overBalance && <Alert severity="warning" sx={{ borderRadius: "12px" }}>{t("wallet.admin.exchangeInsufficient")}</Alert>}
          {tooSmall && <Alert severity="warning" sx={{ borderRadius: "12px" }}>{t("wallet.admin.exchangeTooSmall")}</Alert>}
          {!rate && open && <Alert severity="warning" sx={{ borderRadius: "12px" }}>{t("wallet.admin.exchangeRateUnavailable")}</Alert>}
          {error && <Alert severity="error" sx={{ borderRadius: "12px" }}>{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.25, sm: 3 }, pb: 2.5, pt: 0 }}>
        <Button variant="outlined" color="secondary" onClick={onClose} disabled={submitting} sx={{ borderRadius: "12px", fontWeight: 700, px: 2.5 }}>
          {t("common.cancel")}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" color="secondary" onClick={handleSubmit} disabled={!canSubmit} startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined} sx={{ borderRadius: "12px", fontWeight: 700, px: 3 }}>
          {t("wallet.admin.exchangeConvert")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
